/**
 * 全応答に載せるセキュリティヘッダー。**この表が唯一の正。**
 *
 * 載せる経路は 2 つある。SPA は Workers の assets として配信されており、
 * wrangler.jsonc の run_worker_first に挙げた経路以外は Worker に届かない
 * ので、Hono のミドルウェアでは HTML や JS/CSS の応答に触れられない。
 *   - Worker の応答 : apps/api/src/middleware/security-headers.ts
 *   - 静的アセット  : apps/web/public/_headers (Cloudflare が解釈する)
 * 2 つが食い違わないことは packages/shared/test/security-headers.test.ts が
 * 機械的に見ている。
 */

/**
 * CSP。**外部オリジンは 1 つも無い。** 書体も自前配信で
 * (apps/web/public/fonts/、apps/web/index.html が読む)、CDN に戻すと落ちる。
 *
 * style-src に `'unsafe-inline'` は**要らない**。style-src が縛るのは
 * `<style>` 要素 (style-src-elem) と `style=""` 属性 (style-src-attr) で、
 * **React の style prop は CSSOM 経由なので CSP の対象外**。メーターの幅も
 * ターミナル演出の animationDelay もこれに当たり、落ちない。実ブラウザで
 * 違反 0 件を確認済み
 * (docs/superpowers/specs/2026-08-15-csp-tightening-design.md)。
 * **緩めるときは、緩めないと壊れる画面を実測で示してからにする。**
 *
 * default-src があれば style-src / font-src / connect-src は冗長だが、
 * 「閉じ忘れ」と「意図して閉じた」を読むだけで区別できるよう明示して残す。
 */
const CONTENT_SECURITY_POLICY = [
  "default-src 'self'",
  "base-uri 'self'",
  "object-src 'none'",
  "frame-ancestors 'none'",
  "form-action 'self'",
  "script-src 'self'",
  "style-src 'self'",
  "font-src 'self'",
  "img-src 'self' data:",
  "connect-src 'self'",
].join('; ');

/**
 * 使っていない機能を切る。**Passkey (WebAuthn) だけは明示的に許す。**
 * 既定でも同一オリジンには許可されるが、書いておけば
 * 「Passkey が消えていないこと」を設定を読むだけで確かめられる。
 */
const PERMISSIONS_POLICY = [
  'accelerometer=()',
  'autoplay=()',
  'camera=()',
  'display-capture=()',
  'encrypted-media=()',
  'geolocation=()',
  'gyroscope=()',
  'magnetometer=()',
  'microphone=()',
  'midi=()',
  'payment=()',
  'screen-wake-lock=()',
  'usb=()',
  'xr-spatial-tracking=()',
  'publickey-credentials-create=(self)',
  'publickey-credentials-get=(self)',
].join(', ');

export const SECURITY_HEADERS: Readonly<Record<string, string>> = Object.freeze({
  'Content-Security-Policy': CONTENT_SECURITY_POLICY,
  /*
   * preload は付けない (一度載せると取り消しに時間がかかる)。
   * includeSubDomains は運営ホストも HTTPS 固定にするので望ましい。
   * http の localhost では仕様上ブラウザが無視するため、開発は壊れない。
   */
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
  'X-Content-Type-Options': 'nosniff',
  /** CSP の frame-ancestors の後方互換 */
  'X-Frame-Options': 'DENY',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': PERMISSIONS_POLICY,
});

/**
 * 書体だけのキャッシュ規則。Workers の assets は public/ のファイルに
 * `public, max-age=0, must-revalidate` を付ける (実測) ため、LP のように
 * サブセットを 30 本以上取りに来る画面で毎回それだけ再検証が走る。
 *
 * ファイル名は内容ハッシュではないので、**パスの版 (`/fonts/v1/`) が
 * `immutable` の根拠**になる。書体を更新するときは版を上げる
 * (apps/web/scripts/fetch-fonts.mjs の OUT_VERSION と apps/web/index.html)。
 */
const FONT_CACHE_CONTROL = 'public, max-age=31536000, immutable';

/**
 * Cloudflare の `_headers` 形式に整形する。
 * 先頭の行がパス、続く行が 2 スペース字下げの `名前: 値`。
 *
 * **規則は 2 本ある。** Cloudflare は一致した規則を**全部**適用するので、
 * `/fonts/*` に来た応答にも `/*` のセキュリティヘッダーが載る (置き換えでは
 * ない)。仕様ではなく実装に依存しているので、E2E で実測に留めてある
 * (e2e/tests/security-headers.spec.ts)。
 */
export function renderHeadersFile(): string {
  const lines = ['/*'];
  for (const [name, value] of Object.entries(SECURITY_HEADERS)) {
    lines.push(`  ${name}: ${value}`);
  }
  lines.push('', '/fonts/*', `  Cache-Control: ${FONT_CACHE_CONTROL}`);
  return `${lines.join('\n')}\n`;
}
